'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Form } from '@kit/ui/form';
import { cn } from '@kit/ui/utils';
import type { UploadResult } from '@kit/uploads';

import { toApplicationGroups } from '../../lib/application-structure';
import { isFieldVisible } from '../../lib/form-conditions';
import type {
  ApplicationFieldConfig,
  ApplicationStructure,
  FieldOption,
} from '../../types/audition.types';
import {
  DynamicFormRenderer,
  type AgeCategoryContext,
} from './dynamic-form-renderer';
import type { CountryFieldRenderArgs } from './dynamic-form-renderer-choice-fields';

/** Position + identity of a stepper step, passed to the analytics hooks. */
export interface StepperStepInfo {
  /** Zero-based step position. */
  index: number;
  /** Structure group key (falls back to `step-<index>` for keyless groups). */
  key: string;
  /** Structure group display name. */
  name: string;
  /** Total step count. */
  total: number;
}

interface StepperFormRendererProps {
  structure: ApplicationStructure;
  form: UseFormReturn<Record<string, unknown>>;
  /** Called when the applicant submits on the last step (after validation). */
  onSubmit?: () => void;
  submitLabel?: string;
  isSubmitting?: boolean;
  /** Passed through to DynamicFormRenderer for `file`/`video` fields. */
  uploadFile?: (formData: FormData) => Promise<UploadResult>; // i18n-ignore TS generic type, not UI prose
  deleteFile?: (params: { mediaId: string }) => Promise<UploadResult>; // i18n-ignore TS generic type, not UI prose
  /** Render slot for `experience` fields — see DynamicFormRenderer. */
  renderExperienceField?: (field: ApplicationFieldConfig) => React.ReactNode;
  /** Render slot for the `competition_styles` field — see DynamicFormRenderer. */
  renderStylesField?: (args: {
    field: ApplicationFieldConfig;
    value: string[];
    onChange: (ids: string[]) => void;
  }) => React.ReactNode;
  /** Context for the `age_category` derived field — see DynamicFormRenderer. */
  ageCategoryContext?: AgeCategoryContext;
  /** Option lists for choice fields keyed by field.key — see DynamicFormRenderer. */
  dynamicOptions?: Record<string, FieldOption[]>;
  /** Render slot for country-backed choice fields — see DynamicFormRenderer. */
  renderCountryField?: (args: CountryFieldRenderArgs) => React.ReactNode;
  /** Render slot for the `institution_preferences` field — see DynamicFormRenderer. */
  renderInstitutionPreferencesField?: (
    field: ApplicationFieldConfig,
  ) => React.ReactNode;
  /**
   * Read-only view: disables all field inputs but keeps Back/Next navigation
   * working (so a viewer can page through every step) and hides Submit. Next
   * skips validation in this mode.
   */
  readOnly?: boolean;
  /**
   * Zero-based step to open on (restored from the `?step=` URL param by the
   * server). The current step is written back to `?step=<1-based>` on every
   * transition so a refresh or a shared link reopens the same step. Clamped to
   * the valid range at render. Defaults to the first step.
   */
  initialStep?: number;
  /**
   * Observe-only analytics hook: fires when a step becomes visible — the first
   * step on mount, then every forward/backward transition. Never affects
   * navigation.
   */
  onStepView?: (step: StepperStepInfo) => void;
  /**
   * Observe-only analytics hook: fires when Next (current step) or Submit
   * (full form) validation fails, with the keys of the invalid fields. Never
   * affects navigation.
   */
  onStepBlocked?: (step: StepperStepInfo, invalidKeys: string[]) => void;
}

/**
 * Renders an application form as a multi-step stepper: one step per structure
 * group (derived via `toApplicationGroups`). Each step's fields are rendered
 * through `DynamicFormRenderer` (so media/video/all field types are reused).
 *
 * Improvements over the Meteor renderer: **Next gates on the current step's
 * validity** (Meteor's Next never validated — only Submit did). Submit runs
 * full-form validation before calling `onSubmit`.
 *
 * Mirrors the Meteor `ApplicationFormDialog` stepper (parity map §9).
 */
export function StepperFormRenderer({
  structure,
  form,
  onSubmit,
  submitLabel,
  isSubmitting = false,
  uploadFile,
  deleteFile,
  renderExperienceField,
  renderStylesField,
  ageCategoryContext,
  dynamicOptions,
  renderCountryField,
  renderInstitutionPreferencesField,
  readOnly = false,
  initialStep = 0,
  onStepView,
  onStepBlocked,
}: StepperFormRendererProps) {
  const { t } = useTranslation();
  const groups = useMemo(() => toApplicationGroups(structure), [structure]);

  const [stepIndex, setStepIndex] = useState(initialStep);
  // Clamp at render so a shrinking structure can never leave stepIndex out of
  // range (review L2). Done inline rather than in an effect to avoid the
  // setState-in-effect cascading-render warning.
  const safeIndex = Math.min(stepIndex, Math.max(0, groups.length - 1));
  const currentGroup = groups[safeIndex];
  const isFirst = safeIndex === 0;
  const isLast = safeIndex === groups.length - 1;

  // Each step renders in place by swapping the current group's fields, so the
  // document keeps its prior scroll offset on a step change. After a tall step
  // (e.g. Top 5 School Selection) the next step would otherwise open scrolled
  // past its title and instructions. Reset to the top of the document scroller
  // (the <body>, per the single-scroll-container architecture) on every step
  // transition so the new step's heading and fields are always in view.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [safeIndex]);

  // Persist the current step in the URL (`?step=<1-based>`) so a refresh or a
  // shared link reopens the applicant on the same step (the server reads it
  // back into `initialStep`). Uses history.replaceState — NOT a Next
  // navigation — so a step change never re-runs the page's heavy server
  // component, and browser Back exits the wizard rather than paging through
  // steps (the in-form Back button owns step navigation). Other query params
  // (e.g. `applicationId`) are preserved.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const nextStep = String(safeIndex + 1);
    if (params.get('step') === nextStep) return;
    params.set('step', nextStep);
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}?${params.toString()}`,
    );
  }, [safeIndex]);

  const stepInfo = useMemo<StepperStepInfo>(
    () => ({
      index: safeIndex,
      key: currentGroup?.key || `step-${safeIndex}`,
      name: currentGroup?.name ?? '',
      total: groups.length,
    }),
    [safeIndex, currentGroup, groups.length],
  );

  // Report each step exactly once per visit (mount + every transition). The
  // ref guard keeps a parent re-render — or an unstable onStepView identity —
  // from double-firing the same step.
  const lastViewedStep = useRef(-1);
  useEffect(() => {
    if (lastViewedStep.current === stepInfo.index) return;
    lastViewedStep.current = stepInfo.index;
    onStepView?.(stepInfo);
  }, [stepInfo, onStepView]);

  // Render only the current step's fields by feeding DynamicFormRenderer a
  // sub-structure scoped to this step.
  const stepStructure = useMemo<ApplicationStructure>(
    () => ({
      version: structure.version,
      fields: currentGroup?.fields ?? [],
      settings: structure.settings,
    }),
    [structure.version, structure.settings, currentGroup],
  );

  const goNext = useCallback(async () => {
    // Read-only viewers page through steps without validation gating.
    if (readOnly) {
      setStepIndex((i) => Math.min(i + 1, groups.length - 1));
      return;
    }
    // Only validate fields that are actually visible — a required field hidden
    // by an unmet display condition must not block Next (review M4).
    const values = form.getValues();
    const keys = (currentGroup?.fields ?? [])
      .filter((f) => isFieldVisible(f, values))
      .map((f) => f.key);
    const valid = keys.length === 0 ? true : await form.trigger(keys);
    if (valid) {
      setStepIndex((i) => Math.min(i + 1, groups.length - 1));
    } else {
      onStepBlocked?.(
        stepInfo,
        keys.filter((k) => form.getFieldState(k).invalid),
      );
    }
  }, [currentGroup, form, groups.length, readOnly, onStepBlocked, stepInfo]);

  const goPrev = useCallback(() => {
    setStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  const handleSubmit = useCallback(async () => {
    const valid = await form.trigger();
    if (valid) {
      onSubmit?.();
    } else {
      onStepBlocked?.(stepInfo, Object.keys(form.formState.errors));
    }
  }, [form, onSubmit, onStepBlocked, stepInfo]);

  if (groups.length === 0) return null;

  return (
    // FormProvider context: the nested DynamicFormRenderer renders <FormField>s
    // whose useFormField() reads useFormContext(). Without this wrapper they
    // throw "useFormField must be used within a Form component" and the whole
    // stepper crashes into the error boundary. The form is owned by the caller
    // and passed in; we provide its context here so any consumer is safe.
    <Form {...form}>
      <div className="space-y-6">
        {/* Step indicator */}
        <div className="space-y-2">
          <div className="text-muted-foreground flex items-center justify-between text-xs">
            <span>
              {t('auditions:stepper.stepIndicator', {
                current: safeIndex + 1,
                total: groups.length,
              })}
            </span>
            <span className="font-medium">{currentGroup?.name}</span>
          </div>
          <div className="flex gap-1">
            {groups.map((g, i) => (
              <div
                key={g.key || `step-${i}`}
                className={cn(
                  'h-1 flex-1 rounded-full transition-colors',
                  i <= safeIndex ? 'bg-primary' : 'bg-muted',
                )}
              />
            ))}
          </div>
        </div>

        {/* Current step fields. In read-only mode a disabled fieldset disables
          every input while leaving the navigation buttons (below, outside the
          fieldset) interactive. */}
        {readOnly ? (
          <fieldset disabled className="m-0 border-0 p-0">
            <DynamicFormRenderer
              structure={stepStructure}
              form={form}
              uploadFile={uploadFile}
              deleteFile={deleteFile}
              renderExperienceField={renderExperienceField}
              renderStylesField={renderStylesField}
              ageCategoryContext={ageCategoryContext}
              dynamicOptions={dynamicOptions}
              renderCountryField={renderCountryField}
              renderInstitutionPreferencesField={
                renderInstitutionPreferencesField
              }
            />
          </fieldset>
        ) : (
          <DynamicFormRenderer
            structure={stepStructure}
            form={form}
            uploadFile={uploadFile}
            deleteFile={deleteFile}
            renderExperienceField={renderExperienceField}
            renderStylesField={renderStylesField}
            ageCategoryContext={ageCategoryContext}
            dynamicOptions={dynamicOptions}
            renderCountryField={renderCountryField}
            renderInstitutionPreferencesField={
              renderInstitutionPreferencesField
            }
          />
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between border-t pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={goPrev}
            disabled={isFirst || isSubmitting}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            {t('auditions:stepper.back')}
          </Button>
          {isLast ? (
            // Read-only viewers have no submit affordance.
            readOnly ? (
              <span />
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? t('auditions:stepper.submitting')
                  : (submitLabel ?? t('auditions:stepper.submit'))}
              </Button>
            )
          ) : (
            <Button type="button" onClick={goNext} disabled={isSubmitting}>
              {t('auditions:stepper.next')}
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </Form>
  );
}
